import axios from './api';

import {
  formatTranslationItems,
  transformTranslatedItems,
  transformTranslationLanguages,
} from './utilities/translations';

/**
 * @typedef {Object} TranslationQuestionItems
 * @property {String} [text]
 * @property {Object.<string, string>} [options]
 * @property {Object.<string, string>} [properties]
 * @property {Object.<string, string>} [subQuestions]
 */

/**
 * @typedef {Object} TranslationItems
 * @property {Object.<string, string>} [concepts]
 * @property {Object.<string, TranslationQuestionItems>} [questions]
 */

/**
 * @typedef {Object} TranslationLanguageOption
 * @property {String} label
 * @property {String} value
 */

/**
 * Bulk-translates a survey-shaped set of strings through SurveyGPT. Callers pass bare strings;
 * request formatting wraps each value in `text` or `rich_text`, and response transformation unwraps
 * translated values back to the same shape.
 *
 * @param {Object} options
 * @param {String} [options.fromLang] source language code (e.g. 'en'); optional
 * @param {Boolean} [options.isRichText] whether the strings are rich text (HTML) — chooses the
 *   `rich_text` field over `text` per string
 * @param {TranslationItems} options.items
 * @param {AbortSignal} [options.signal]
 * @param {String} options.toLang target language code (e.g. 'fr')
 * @return {Promise<TranslationItems>}
 */
export const translateBulk = async ({ fromLang, isRichText, items, signal, toLang }) => {
  const fieldKey = isRichText ? 'rich_text' : 'text';
  const { data } = await axios({
    data: {
      ...(fromLang ? { from_language: fromLang } : {}),
      items: [formatTranslationItems(items, fieldKey)],
      to_language: toLang,
    },
    method: 'post',
    signal,
    url: '/surveygpt/translate/bulk/',
  });
  const translated = Array.isArray(data.items) ? data.items[0] || {} : data.items || {};
  return transformTranslatedItems(translated, fieldKey);
};

/**
 * Fetch the translation languages supported by the SurveyGPT translate service.
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 * @return {Promise<TranslationLanguageOption[]>}
 */
export const fetchTranslationLanguages = async ({ signal } = {}) => {
  const { data } = await axios({
    method: 'get',
    signal,
    url: '/surveygpt/translate/languages/',
  });

  const payload = data?.languages;
  return transformTranslationLanguages(payload);
};
